import React, { useState } from "react";

// Utility function to compare objects
import areObjectsEqual from '../../../../utils/areObjectsEqual';

// Helper function to validate email format
import ValidateEmail from "../../../../helpers/validateEmail";

// Global error hook
import { useError } from "../../../../context/ErrorContextProvider";

const Form = ({
  handleSubmit,          // Function to handle form submission
  formData,              // Form data state passed down from the parent
  setFormData,           // Function to update form data
  copyData,              // Copy of initial data to track unsaved changes
  isWalletConnected,     // Boolean indicating if the wallet is connected
}) => {
  const { showError } = useError();

  const MAX_CHAR_LIMIT_24 = 24;
  const MAX_CHAR_LIMIT = 100;
  
  // Error states for form validation
  const [errorSponsorName, setErrorSponsorName] = useState(false);
  const [errorSponsorEmail, setErrorSponsorEmail] = useState(false);
  const [errorEmailNotValid, setErrorEmailNotValid] = useState(false);

  // States for character limit indication
  const [nameCharLimit, setNameCharLimit] = useState(false);
  const [emailCharLimit, setEmailCharLimit] = useState(false);
  const [addressCharLimit, setAddressCharLimit] = useState(false);

  // Check if there are unsaved changes in the form data
  const hasUnsavedChanges = areObjectsEqual(copyData, formData);

  // Function to handle input changes and enforce character limits
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "sponsor_name" && value.length <= MAX_CHAR_LIMIT_24) {
      // Check character limit for sponsor name
      setNameCharLimit(value.length === MAX_CHAR_LIMIT_24);
      setFormData({ ...formData, [name]: value });
    } 
    else if (name === "sponsor_email" && value.length <= MAX_CHAR_LIMIT) {
      // Check character limit for sponsor email
      setEmailCharLimit(value.length === MAX_CHAR_LIMIT);
      setFormData({ ...formData, [name]: value });
    }
    else if (name === "payment_address" && value.length <= MAX_CHAR_LIMIT) {
      // Check character limit for payment address
      setAddressCharLimit(value.length === MAX_CHAR_LIMIT);
      setFormData({ ...formData, [name]: value });
    }
  };

  // Function to handle form submission when Save Changes is clicked
  const handleClick = () => {
    if (isWalletConnected) {
      // Check if sponsor name is provided
      if (!formData.sponsor_name) {
        setErrorSponsorName(true);
      } else {
        setErrorSponsorName(false);
      }
      
      // Check if sponsor email is provided and valid
      if (!formData.sponsor_email) {
        setErrorSponsorEmail(true);
      } else {
        if (!ValidateEmail(formData.sponsor_email)) {
          setErrorEmailNotValid(true); // Set error if email format is invalid
        } else {
          setErrorEmailNotValid(false);
        }
        setErrorSponsorEmail(false);
      }
      
      // Submit form if all fields are valid
      if (formData.sponsor_name && formData.sponsor_email && ValidateEmail(formData.sponsor_email)) {
        handleSubmit(formData); // Call parent function to handle submission
      }
    } else {
      showError("Connect your wallet"); // Prompt to connect wallet if not connected
    }
  };
  
  return (
    <>
      {/* Sponsor Name Input */}
      <div>
        <label htmlFor="sponsor_name" className="text-gray form-labels">Sponsor name:</label>
        <input
          type="text"
          name="sponsor_name"
          onChange={handleChange}
          value={formData.sponsor_name}
          className="form-control mt-2 inputs"
          id="sponsor_name"
          disabled={!isWalletConnected}
          maxLength={MAX_CHAR_LIMIT_24}
        />
        {errorSponsorName && (
          <span id="errorSponsorName" className="form-text text-danger">
            Sponsor name is required
          </span>
        )}
        {nameCharLimit && (
          <span className="form-text text-danger">Maximum character limit reached</span>
        )}
      </div>

      {/* Sponsor Email Input */}
      <div className="mt-4">
        <label htmlFor="sponsor_email" className="text-gray form-labels">Email:</label>
        <input
          type="text"
          name="sponsor_email"
          id="sponsor_email"
          onChange={handleChange}
          value={formData.sponsor_email}
          className="form-control mt-2 inputs"
          disabled={!isWalletConnected}
          maxLength={MAX_CHAR_LIMIT}
        />
        {errorSponsorEmail && (
          <span id="errorSponsorEmail" className="form-text text-danger">
            Sponsor email is required
          </span>
        )}
        {emailCharLimit && (
          <span className="form-text text-danger">Maximum character limit reached</span>
        )}
        {errorEmailNotValid && (
          <span id="errorSponsorEmail" className="form-text text-danger">
            Enter a valid email format
          </span>
        )}
      </div>

      {/* Payment Address Input */}
      <div className="mt-4">
        <label htmlFor="payment_address" className="text-gray form-labels">Payment Address:</label>
        <input
          type="text"
          name="payment_address"
          id="payment_address"
          onChange={handleChange}
          value={formData.payment_address}
          className="form-control mt-2 inputs"
          disabled={!isWalletConnected}
          maxLength={MAX_CHAR_LIMIT}
        />
        {addressCharLimit && (
          <span className="form-text text-danger">Maximum character limit reached</span>
        )}
      </div>

      {/* Save Changes Button */}
      <div className="mt-5">
        <input
          type="submit"
          value="Save Changes"
          className="btn form-btn bg-orange"
          onClick={handleClick}
          disabled={!isWalletConnected || hasUnsavedChanges} // Disable button if no changes or wallet is not connected
        />
      </div>
    </>
  );
};

export default Form;
